import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

type OptionType = {
  value: string;
  label: string;
  disabled?: boolean;
};

/**
 * Hook to filter options based on form mode (create vs edit).
 *
 * In create mode: Only show active options (disabled !== true)
 * In edit mode: Show active options + current inactive value (if present, as disabled)
 *
 * @param fieldName - The name of the form field
 * @param options - Array of options with value, label, and optional disabled flag
 * @returns Filtered options array
 */
export const useFilteredOptions = (
  fieldName: string,
  options: OptionType[]
): OptionType[] => {
  const { watch } = useFormContext();
  const isEditMode = watch("_isEditMode") || false;
  const fieldValue = watch(fieldName);

  return useMemo(() => {
    if (!isEditMode) {
      // Create mode: only active options
      return options.filter(opt => !opt.disabled);
    }

    // Edit mode: active + current value if inactive
    const activeOptions = options.filter(opt => !opt.disabled);
    const currentOption = options.find(opt => opt.value === fieldValue);

    if (currentOption && currentOption.disabled) {
      // Current value is inactive - include it as disabled
      return [...activeOptions, currentOption];
    }

    return activeOptions;
  }, [isEditMode, options, fieldValue]);
};

